import React, { PureComponent, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Popover } from 'react-tiny-popover';

import FieldTableCustomColumnIds from '../../../constants/FieldTableCustomColumnIds';
import DecoratorIconOptions from '../../../constants/DecoratorIconOptions';

import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

import Icon from '../../misc/Icon';

const StyledRow = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledTH = styled.div`
  box-sizing: border-box;
  background-color: ${(props) => props.theme.secondaryColor};
  font-weight: 600;
  font-size: 12px;
  color: ${(props) => props.theme.fontColorPrimary};
  border-bottom: 1px solid ${(props) => props.theme.borderColor};
  border-right: 1px solid ${(props) => props.theme.borderColor};
  padding: 8px;
  max-width: ${(props) => (props.width !== undefined ? `${props.width}px` : 'initial')};
  min-width: ${(props) => (props.width !== undefined ? `${props.width}px` : 'initial')};
  vertical-align: top;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  svg {
    font-size: 12px;
  }
`;

const StyledTitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  min-width: 0;
`;

const StyledOperand = styled.span`
  font-weight: 600;
  color: ${(props) => props.theme.fontColorSecondary};
  padding-right: 4px;
  flex-shrink: 0;
`;

const StyledTitle = styled.span`
  display: inline-block;
  flex: 1 1 auto;
  min-width: 40px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledIconWrapper = styled.span`
  padding-left: 4px;
  flex-shrink: 0;
  svg {
    font-size: 14px;
  }
`;

const StyledActionsWrapper = styled.span`
  white-space: nowrap;
  flex-shrink: 0;
`;

const StyledDecoratorPill = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 1;
  min-width: 0;
  max-width: 50%;
  gap: ${(props) => (props.$hasLabel ? '3px' : '0')};
  padding: ${(props) => (props.$hasLabel ? '1px 5px' : '3px 5px')};
  margin-left: 4px;
  border-radius: 3px;
  border: 1px solid ${(props) => props.$color};
  font-size: 10px;
  font-weight: 500;
  color: ${(props) => props.$color};
  cursor: default;
  white-space: nowrap;
  overflow: hidden;
  line-height: ${(props) => (props.$hasLabel ? '1.4' : '1')};

  svg {
    font-size: 10px;
    flex-shrink: 0;
  }
`;

const StyledDecoratorPillLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const StyledKebabTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: default;
  background: none;
  border: none;
  padding: 1px 2px;
  margin-left: 4px;
  border-radius: 3px;
  opacity: 0.55;

  &:focus {
    outline: none;
  }

  svg {
    font-size: 12px;
  }
`;

const StyledMenu = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.$bg};
  border-radius: ${(props) => props.$radius}px;
  border: 1px solid ${(props) => props.$border};
  box-shadow: 0px 4px 20px rgba(20, 20, 23, 0.18);
  min-width: 160px;
  max-width: 240px;
  overflow: hidden;
  font-family: ${(props) => props.$fontFamily};
`;

const StyledMenuRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;

  & + & {
    border-top: 1px solid ${(props) => props.$border};
  }
`;

const StyledMenuIconWrapper = styled.div`
  flex-shrink: 0;
  width: 14px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 12px;
  }
`;

const StyledMenuLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${(props) => props.$color};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
`;

const getIconDefinition = (iconValue) => {
  if (!iconValue) return null;
  const match = DecoratorIconOptions.find((opt) => opt.value === iconValue);
  return match ? match.icon : ThemeIcons.decorator.image;
};

/**
 * Functional component for rendering column decorators inline in the editor header.
 */
const EditorColumnDecorators = ({ decorators, primaryColor, theme }) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!decorators || !Array.isArray(decorators) || decorators.length === 0) return null;

  const validDecorators = decorators.filter((d) => !d.deleted && (d.icon || d.label));
  if (validDecorators.length === 0) return null;

  if (validDecorators.length === 1) {
    const decorator = validDecorators[0];
    const iconDef = getIconDefinition(decorator.icon);
    const color = decorator.color || primaryColor;
    return (
      <StyledDecoratorPill $color={color} $hasLabel={!!decorator.label}>
        {iconDef ? <Icon icon={iconDef} color={color} /> : null}
        {decorator.label ? <StyledDecoratorPillLabel>{decorator.label}</StyledDecoratorPillLabel> : null}
      </StyledDecoratorPill>
    );
  }

  const menuContent = (
    <StyledMenu
      $bg={theme.backgroundColor}
      $radius={theme.borderRadius}
      $border={theme.borderColor}
      $fontFamily={theme.fontFamily}
    >
      {validDecorators.map((decorator, index) => {
        const iconDef = getIconDefinition(decorator.icon);
        const color = decorator.color || primaryColor;
        return (
          <StyledMenuRow
            key={decorator._id || index}
            $border={theme.borderColor}
          >
            {iconDef ? (
              <StyledMenuIconWrapper>
                <Icon icon={iconDef} color={color} />
              </StyledMenuIconWrapper>
            ) : null}
            {decorator.label ? (
              <StyledMenuLabel $color={color}>{decorator.label}</StyledMenuLabel>
            ) : null}
          </StyledMenuRow>
        );
      })}
    </StyledMenu>
  );

  return (
    <Popover
      isOpen={isOpen}
      positions={['bottom', 'top', 'right', 'left']}
      reposition={true}
      containerStyle={{ zIndex: '2147483647' }}
      onClickOutside={() => setIsOpen(false)}
      content={menuContent}
    >
      <StyledKebabTrigger
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
      >
        <Icon icon={ThemeIcons.misc.decorators} />
      </StyledKebabTrigger>
    </Popover>
  );
};

class TableEditorHeader extends PureComponent {

  static contextType = ThemeContext

  static propTypes = {

    id: PropTypes.string,
    columns: PropTypes.array,

    //Action handlers
    onEdit: PropTypes.func,
    onMoveLeft: PropTypes.func,
    onMoveRight: PropTypes.func,

  }

  render() {

    const {
      id,
      columns,
      fieldPositionColumns,

      //Action handlers
      onMoveLeft,
      onMoveRight,
      onEdit,

    } = this.props;

    const { borderColor, primaryColor } = this.context;

    const columnNodes = columns.map((column, columnIndex) => {

      const fieldPositionColumn = fieldPositionColumns?.[column._id] || {};
      const isUtilityColumn = (
        column._id === FieldTableCustomColumnIds.rowMenu ||
        column._id === FieldTableCustomColumnIds.rowNumber ||
        column._id === FieldTableCustomColumnIds.rowDecorators
      );

      let columnWidth;
      if (column._id === FieldTableCustomColumnIds.rowDecorators) {
        columnWidth = column.width ?? 80;
      } else if (isUtilityColumn) {
        columnWidth = 24;
      } else {
        columnWidth = 200;
      }

      const columnEdit = isUtilityColumn ? null : (
        <StyledIconWrapper>
          <Icon
            id={`${id}-${column._id}-table-editor-column-edit`}
            color={borderColor}
            hoverColor={primaryColor}
            icon={ThemeIcons.settings.settings}
            onClick={() => onEdit(column._id)}
          />
        </StyledIconWrapper>
      );

      const columnMoveLeft = isUtilityColumn || columnIndex === 2 ? null : (
        <StyledIconWrapper>
          <Icon
            id={`${id}-${column._id}-table-editor-column-move-left`}
            color={borderColor}
            hoverColor={primaryColor}
            size="lg"
            icon={ThemeIcons.tableSettings.columnMoveLeft}
            onClick={() => onMoveLeft(column._id)}
          />
        </StyledIconWrapper>
      );

      const columnMoveRight = isUtilityColumn || columnIndex === columns.length - 1 ? null : (
        <StyledIconWrapper>
          <Icon
            id={`${id}-${column._id}-table-editor-column-move-right`}
            color={borderColor}
            size="lg"
            hoverColor={primaryColor}
            icon={ThemeIcons.tableSettings.columnMoveRight}
            onClick={() => onMoveRight(column._id)}
          />
        </StyledIconWrapper>
      );

      return (
        <StyledTH
          theme={this.context}
          key={column._id}
          data-testid={`${id}-${column._id}-${columnIndex}-table-editor-column-th`}
          {...{ width: columnWidth }}
        >
          <StyledTitleWrapper>
            {isUtilityColumn ? null : (
              <StyledOperand theme={this.context}>
                {column.operand}
                :
              </StyledOperand>
            )}
            <>
              {(column.hidden || column.hiddenViews?.length > 0 || column.logic?.conditions?.length > 0) ? (<Icon icon={ThemeIcons.settings.hide} color={this.context.warningColor} size="sm"/>) : null}
              <StyledTitle>{column.title}</StyledTitle>
              {!isUtilityColumn ? (
                <EditorColumnDecorators
                  decorators={column.decorators}
                  primaryColor={primaryColor}
                  theme={this.context}
                />
              ) : null}
              <StyledActionsWrapper>
                {columnMoveLeft}
                {columnMoveRight}
                {columnEdit}
              </StyledActionsWrapper>
            </>
          </StyledTitleWrapper>
        </StyledTH>
      );

    });

    return (
      <StyledRow>
        {columnNodes}
      </StyledRow>
    );

  }

}

export default TableEditorHeader;
